import { ChakraProvider } from "@chakra-ui/react";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import React, { useEffect, useState } from "react";
import { Toaster } from "sonner";
import { ThemeProvider } from "styled-components";
import Routes from "./Routes";
import ContactContextMenu from "./components/Shared/ContactContextMenu/ContactContextMenu";
import { PERSIST_MAX_AGE, queryClient, queryPersister } from "./networking/api-hooks";
import { useAuthStore } from "./stores/auth.store";
import { customChakraTheme } from "./theme/ChakraTheme";
import GlobalStyles from "./theme/GlobalStyles";
import { appTheme } from "./theme/Theme";

const App: React.FC = () => {
	const [initialized, setInitialized] = useState(false);

	useEffect(() => {
		async function init() {
			try {
				await useAuthStore.getState().initLocalStorage();
			} catch (err) {
				// eslint-disable-next-line no-console
				console.warn("Failed initializing");
			} finally {
				setInitialized(true);
			}
		}

		init();
	}, []);

	return (
		<PersistQueryClientProvider
			client={queryClient}
			persistOptions={{
				persister: queryPersister,
				maxAge: PERSIST_MAX_AGE,
				// Deployed git hash (injected at deploy time, see scripts/set-git-version.cjs).
				// A new deploy gets a new buster and drops previously persisted caches.
				buster: window.gitVersion || "dev",
				dehydrateOptions: {
					// Persist only the organisation entity, the current user (incl. their
					// UI preferences) and the full consultant/customer lists — not the
					// rest of the cache.
					shouldDehydrateQuery: (query) => {
						if (query.state.status !== "success") return false;
						const [scope, sub] = query.queryKey as unknown[];
						if (scope === "current-user") return true;
						// useOrganisation keys as ["organisation", queryObj]; other org-scoped
						// queries put a string at [1] (e.g. "notification-settings").
						if (scope === "organisation" && (sub === undefined || typeof sub === "object"))
							return true;
						// The full per-pool lists (useAllConsultants/useAllCustomers) —
						// revalidated via sync versions (see useSyncVersionWatcher).
						if ((scope === "consultants" || scope === "customers") && sub === "all") return true;
						return false;
					},
				},
			}}
		>
			<ChakraProvider resetCSS={false} theme={customChakraTheme}>
				<ThemeProvider theme={appTheme}>
					<Toaster
						position="top-right"
						closeButton
						toastOptions={{
							classNames: {
								toast: "rounded-xl shadow-lg",
								title: "font-semibold",
								description: "text-gray-500",
							},
						}}
					/>
					{initialized && <Routes />}
					<GlobalStyles />
					<link
						type="text/css"
						href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap"
						rel="stylesheet"
					></link>
					<ContactContextMenu />
				</ThemeProvider>
			</ChakraProvider>
		</PersistQueryClientProvider>
	);
};

export default App;
